import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Users,
  UserCheck,
  Play,
  X,
  QrCode,
  Copy,
  Smartphone,
  Plus,
  Search,
  MoreHorizontal,
  Eye,
  LayoutGrid,
  List,
  ChevronLeft,
  ChevronRight,
  Lock,
  Sparkles,
} from "lucide-react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button, FloatingActionButton } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useIsMobile } from "@/hooks/useMobileUtils";
import { useAuthStore } from "@/store/auth.store";
import { formatDateShort } from "@/utils/dateFormat";
import { inPersonApi, envelopeApi } from "../../services/api";

// Pagination component
const Pagination = ({ currentPage, totalPages, onPageChange }) => {
  const { t } = useTranslation();

  if (totalPages <= 1) return null;

  const getPageNumbers = () => {
    const pages = [];
    const showPages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(showPages / 2));
    const endPage = Math.min(totalPages, startPage + showPages - 1);

    if (endPage - startPage + 1 < showPages) {
      startPage = Math.max(1, endPage - showPages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  return (
    <div className="flex items-center justify-between">
      <p className="text-sm text-muted-foreground">
        {t("common.page")} {currentPage} {t("common.of")} {totalPages}
      </p>
      <div className="flex items-center gap-1">
        <Button
          variant="outline"
          size="sm"
          disabled={currentPage <= 1}
          onClick={() => onPageChange(currentPage - 1)}
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          {t("common.previous")}
        </Button>
        <div className="flex gap-1">
          {getPageNumbers().map((pageNum) => (
            <Button
              key={pageNum}
              variant={currentPage === pageNum ? "default" : "outline"}
              size="sm"
              className="w-8 h-8 p-0"
              onClick={() => onPageChange(pageNum)}
            >
              {pageNum}
            </Button>
          ))}
        </div>
        <Button
          variant="outline"
          size="sm"
          disabled={currentPage >= totalPages}
          onClick={() => onPageChange(currentPage + 1)}
        >
          {t("common.next")}
          <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </div>
    </div>
  );
};

// ============================================
// UpgradeRequired Component
// ============================================
const UpgradeRequired = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold font-heading tracking-tight">
          {t("inPerson.title", "In-Person Signing")}
        </h1>
        <p className="text-muted-foreground">
          {t("inPerson.subtitle", "Facilitate face-to-face signing sessions")}
        </p>
      </div>

      {/* Single Card with divider */}
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-16 text-center">
          <div className="flex h-20 w-20 items-center justify-center rounded-full bg-primary/10 mb-6">
            <Lock className="h-10 w-10 text-primary" />
          </div>
          <h2 className="text-2xl font-semibold mb-2">
            {t("upgrade.featureNotAvailable", "Feature Not Available")}
          </h2>
          <p className="text-muted-foreground mb-6 max-w-md">
            {t(
              "upgrade.inPersonSigningUpgradeDesc",
              "In-Person Signing allows you to host signing sessions where signers complete documents on a shared device.",
            )}
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Button onClick={() => navigate("/settings/billing")}>
              <Sparkles className="h-4 w-4 mr-2" />
              {t("upgrade.upgradePlan", "Upgrade Plan")}
            </Button>
            <Button variant="outline" onClick={() => navigate("/envelopes")}>
              {t("common.backToEnvelopes", "Back to Envelopes")}
            </Button>
          </div>

          {/* Feature highlights */}
          <div className="mt-8 pt-8 border-t w-full max-w-lg">
            <div className="w-full flex items-center justify-center gap-2 mb-4">
              <UserCheck className="h-3.5 w-3.5 text-primary" />
              <h3 className="text-sm font-medium text-muted-foreground">
                {t(
                  "inPerson.withInPersonSigningYouCan",
                  "With In-Person Signing you can:",
                )}
              </h3>
            </div>
            <ul className="text-sm text-muted-foreground space-y-1 flex items-center justify-center flex-col">
              <li>
                {t(
                  "inPerson.feature1",
                  "Host signing sessions on a shared device",
                )}
              </li>
              <li>
                {t(
                  "inPerson.feature2",
                  "Guide signers through the process in real-time",
                )}
              </li>
              <li>
                {t(
                  "inPerson.feature3",
                  "Perfect for meetings, notarization, and closings",
                )}
              </li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export const InPersonSigning = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const isMobile = useIsMobile();

  // Get user subscription to check feature access
  const user = useAuthStore((state) => state.user);

  // Check if user has in-person signing feature - must be explicitly true
  const hasInPersonFeature =
    user?.subscription?.features?.inPersonSigning === true;

  // ============================================
  // Local UI State
  // ============================================
  const [searchInput, setSearchInput] = useState(
    searchParams.get("search") || "",
  );
  const [showStartModal, setShowStartModal] = useState(false);
  const [showQRModal, setShowQRModal] = useState(null);
  const [selectedEnvelope, setSelectedEnvelope] = useState("");
  const [hostName, setHostName] = useState("");
  const [copiedId, setCopiedId] = useState(null);
  const [viewMode, setViewMode] = useState("grid");

  // Get filter values from URL
  const currentPage = parseInt(searchParams.get("page") || "1", 10);
  const currentSearch = searchParams.get("search") || "";
  const currentStatus = searchParams.get("status") || "all";

  // ============================================
  // TanStack Queries
  // ============================================

  // Query: Load in-person sessions with pagination (only if feature enabled)
  const { data: sessionsResponse, isLoading: sessionsLoading } = useQuery({
    queryKey: [
      "in-person-sessions",
      { page: currentPage, search: currentSearch, status: currentStatus },
    ],
    queryFn: async () => {
      const response = await inPersonApi.list({
        page: currentPage,
        limit: 12,
        search: currentSearch || undefined,
        status: currentStatus === "all" ? undefined : currentStatus,
      });
      return response.data;
    },
    staleTime: 30 * 1000, // 30 seconds
    enabled: hasInPersonFeature, // Don't fetch if feature is disabled
  });

  const sessions = sessionsResponse?.data || [];
  const pagination = sessionsResponse?.pagination || {
    page: 1,
    limit: 12,
    total: 0,
  };
  const totalPages = Math.ceil(pagination.total / pagination.limit);

  // Query: Load envelopes for starting new sessions (only if feature enabled)
  const { data: envelopesData } = useQuery({
    queryKey: ["envelopes", "for-in-person"],
    queryFn: async () => {
      const response = await envelopeApi.list({
        status: "draft,sent,viewed,partially_signed",
        limit: 100,
      });
      const envelopeList = response.data?.data || [];
      // Filter draft envelopes to only show those with documents and recipients
      return envelopeList.filter((env) => {
        if (env.status === "draft") {
          return env.documentCount > 0 && env.recipientCount > 0;
        }
        return true;
      });
    },
    staleTime: 60 * 1000, // 1 minute
    enabled: hasInPersonFeature, // Don't fetch if feature is disabled
  });

  const envelopes = envelopesData || [];

  // ============================================
  // Mutations
  // ============================================

  // Mutation: Start session
  const startSessionMutation = useMutation({
    mutationFn: async ({ envelopeId, hostName }) => {
      const response = await inPersonApi.start(envelopeId, {
        hostName: hostName || "Host",
      });
      return response.data?.data;
    },
    onSuccess: (session) => {
      if (!session || !session.id) {
        toast.error("Failed to create session. Please try again.");
        return;
      }
      toast.success("In-person session started!");
      setShowStartModal(false);
      setSelectedEnvelope("");
      setHostName("");
      queryClient.invalidateQueries({ queryKey: ["in-person-sessions"] });
      navigate(`/signing/in-person/${session.id}/host`);
    },
    onError: (error) => {
      console.error("Failed to start session:", error);
      const message =
        error.response?.data?.message ||
        error.message ||
        "Failed to start session";
      toast.error(message);
    },
  });

  // Mutation: Cancel session
  const cancelSessionMutation = useMutation({
    mutationFn: (sessionId) => inPersonApi.cancel(sessionId),
    onSuccess: () => {
      toast.success(t("inPerson.sessionCancelledSuccess"));
      queryClient.invalidateQueries({ queryKey: ["in-person-sessions"] });
    },
    onError: (error) => {
      console.error("Failed to cancel session:", error);
      toast.error(t("inPerson.failedToCancelSession"));
    },
  });

  // ============================================
  // Derived State
  // ============================================

  const loading = sessionsLoading;

  // ============================================
  // Event Handlers
  // ============================================

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams(searchParams);
    if (searchInput) {
      params.set("search", searchInput);
    } else {
      params.delete("search");
    }
    params.delete("page");
    setSearchParams(params);
  };

  const handlePageChange = (newPage) => {
    const params = new URLSearchParams(searchParams);
    params.set("page", newPage.toString());
    setSearchParams(params);
  };

  const handleStatusFilter = (status) => {
    const params = new URLSearchParams(searchParams);
    params.set("status", status);
    params.delete("page");
    setSearchParams(params);
  };

  const copyUrl = (session) => {
    navigator.clipboard.writeText(session.url);
    setCopiedId(session.id);
    toast.success("Session URL copied to clipboard!");
    setTimeout(() => setCopiedId(null), 2000);
  };

  const startSession = () => {
    if (!selectedEnvelope) return;
    startSessionMutation.mutate({
      envelopeId: selectedEnvelope,
      hostName,
    });
  };

  const cancelSession = (sessionId) => {
    if (!confirm(t("inPerson.cancelSessionConfirm"))) return;
    cancelSessionMutation.mutate(sessionId);
  };

  // ============================================
  // Helper Functions
  // ============================================

  const getStatusBadge = (session) => {
    switch (session.status) {
      case "active":
        return (
          <Badge variant="outline" className="border-blue-500 text-blue-500">
            {t("status.active", "Active")}
          </Badge>
        );
      case "completed":
        return (
          <Badge variant="default" className="bg-green-500">
            {t("status.completed", "Completed")}
          </Badge>
        );
      case "cancelled":
        return (
          <Badge variant="secondary">
            {t("status.cancelled", "Cancelled")}
          </Badge>
        );
      case "expired":
        return (
          <Badge variant="destructive">{t("status.expired", "Expired")}</Badge>
        );
      default:
        return <Badge variant="secondary">{session.status}</Badge>;
    }
  };

  const renderContent = () => {
    if (sessions.length === 0) {
      return (
        <Card>
          <CardContent className="p-0">
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted mb-4">
                <UserCheck className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold mb-1">
                {currentSearch
                  ? "No sessions found"
                  : "No in-person sessions yet"}
              </h3>
              <p className="text-muted-foreground mb-4">
                {currentSearch
                  ? "Try adjusting your search query"
                  : "Start an in-person signing session when signers are present"}
              </p>
              {!currentSearch && (
                <Button onClick={() => setShowStartModal(true)}>
                  <Plus className="mr-2 h-4 w-4" />
                  Start First Session
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      );
    }
    return viewMode === "grid" ? (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {sessions.map((session) => (
          <Card
            key={session.id}
            className="group hover:shadow-md transition-shadow overflow-hidden"
          >
            {/* Preview area */}
            <div className="h-32 bg-muted flex items-center justify-center relative">
              <div className="text-center">
                <UserCheck className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                {getStatusBadge(session)}
              </div>

              {/* Quick actions overlay */}
              <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="secondary" size="icon" className="h-8 w-8">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {session.status === "active" && (
                      <>
                        <DropdownMenuItem
                          onClick={() =>
                            navigate(`/signing/in-person/${session.id}/host`)
                          }
                        >
                          <Play className="mr-2 h-4 w-4" />
                          Continue Session
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => copyUrl(session)}>
                          <Copy className="mr-2 h-4 w-4" />
                          {copiedId === session.id ? "Copied!" : "Copy URL"}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => setShowQRModal(session)}
                        >
                          <QrCode className="mr-2 h-4 w-4" />
                          Show QR Code
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => cancelSession(session.id)}
                          className="text-destructive"
                        >
                          <X className="mr-2 h-4 w-4" />
                          Cancel Session
                        </DropdownMenuItem>
                      </>
                    )}
                    {session.status !== "active" && (
                      <DropdownMenuItem
                        onClick={() =>
                          navigate(`/envelopes/${session.envelopeId}`)
                        }
                      >
                        <Eye className="mr-2 h-4 w-4" />
                        View Document
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            {/* Content */}
            <CardContent className="p-4 dark:bg-background">
              <h3 className="font-medium truncate mb-1">
                {session.envelopeTitle || "Untitled"}
              </h3>
              <p className="text-sm text-muted-foreground truncate mb-3">
                Host: {session.hostName || "Unknown"}
              </p>

              <div className="flex items-center justify-between text-sm text-muted-foreground">
                <div className="flex items-center gap-1">
                  <Users className="h-4 w-4" />
                  {session.signedCount || 0} / {session.totalSigners || 0}
                </div>
                <span>{formatDateShort(session.startedAt)}</span>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    ) : (
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Document</TableHead>
                <TableHead>Host</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Signers</TableHead>
                <TableHead>Started</TableHead>
                <TableHead className="w-25" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {sessions.map((session) => (
                <TableRow key={session.id}>
                  <TableCell>
                    <div className="font-medium">
                      {session.envelopeTitle || "Untitled"}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div>{session.hostName || "Unknown"}</div>
                  </TableCell>
                  <TableCell>{getStatusBadge(session)}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Users className="h-4 w-4 text-muted-foreground" />
                      {session.signedCount || 0} / {session.totalSigners || 0}
                    </div>
                  </TableCell>
                  <TableCell>{formatDateShort(session.startedAt)}</TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {session.status === "active" && (
                          <>
                            <DropdownMenuItem
                              onClick={() =>
                                navigate(
                                  `/signing/in-person/${session.id}/host`,
                                )
                              }
                            >
                              <Play className="mr-2 h-4 w-4" />
                              Continue Session
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => copyUrl(session)}>
                              <Copy className="mr-2 h-4 w-4" />
                              {copiedId === session.id ? "Copied!" : "Copy URL"}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => setShowQRModal(session)}
                            >
                              <QrCode className="mr-2 h-4 w-4" />
                              Show QR Code
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => cancelSession(session.id)}
                              className="text-destructive"
                            >
                              <X className="mr-2 h-4 w-4" />
                              Cancel Session
                            </DropdownMenuItem>
                          </>
                        )}
                        {session.status !== "active" && (
                          <DropdownMenuItem
                            onClick={() =>
                              navigate(`/envelopes/${session.envelopeId}`)
                            }
                          >
                            <Eye className="mr-2 h-4 w-4" />
                            View Document
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    );
  };

  // ============================================
  // Loading State
  // ============================================

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-4 w-64" />
          </div>
          <Skeleton className="h-10 w-36" />
        </div>
        <Card>
          <CardContent className="p-0">
            {[1, 2, 3, 4, 5].map((i) => (
              <div
                key={i}
                className="flex items-center gap-4 p-4 border-b last:border-0"
              >
                <Skeleton className="h-10 w-10 rounded-lg" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-48" />
                  <Skeleton className="h-3 w-32" />
                </div>
                <Skeleton className="h-6 w-20" />
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    );
  }

  // ============================================
  // Feature Check - Show upgrade message if in-person signing not available
  // ============================================

  if (!hasInPersonFeature) {
    return <UpgradeRequired />;
  }

  // ============================================
  // Main Render
  // ============================================

  return (
    <div className="space-y-6 pb-20 sm:pb-0">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            {t("inPerson.title", "In-Person Signing")}
          </h1>
          <p className="text-muted-foreground text-sm sm:text-base">
            {t(
              "inPerson.description",
              "Host signing sessions where signers are physically present. Perfect for meetings and client visits.",
            )}
          </p>
        </div>
        <Button
          onClick={() => setShowStartModal(true)}
          className="hidden sm:inline-flex"
        >
          <Plus className="mr-2 h-4 w-4" />
          {t("inPerson.startSession", "Start Session")}
        </Button>
      </div>

      {/* Mobile FAB */}
      {isMobile && (
        <FloatingActionButton
          icon={Plus}
          onClick={() => setShowStartModal(true)}
          label={t("inPerson.startSession", "Start Session")}
        />
      )}

      {/* Info Section */}
      <div className="rounded-lg border bg-muted/50 p-4">
        <div className="flex items-start gap-3">
          <div className="rounded-md bg-primary/10 p-2">
            <Smartphone className="h-5 w-5 text-primary" />
          </div>
          <div className="space-y-1">
            <h3 className="font-medium">How In-Person Signing Works</h3>
            <p className="text-sm text-muted-foreground">
              Start a session, hand your device to each signer, and collect
              signatures in real-time. Perfect for notarization, real estate
              closings, and face-to-face meetings.
            </p>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card
          className={`cursor-pointer transition-colors ${
            currentStatus === "all"
              ? "ring-2 ring-primary"
              : "hover:bg-muted/50"
          }`}
          onClick={() => handleStatusFilter("all")}
        >
          <CardHeader className="pb-2">
            <CardDescription>Total Sessions</CardDescription>
            <CardTitle className="text-3xl">
              {pagination.total || sessions.length}
            </CardTitle>
          </CardHeader>
        </Card>
        <Card
          className={`cursor-pointer transition-colors ${
            currentStatus === "active"
              ? "ring-2 ring-primary"
              : "hover:bg-muted/50"
          }`}
          onClick={() => handleStatusFilter("active")}
        >
          <CardHeader className="pb-2">
            <CardDescription>{t("status.active", "Active")}</CardDescription>
            <CardTitle className="text-3xl text-blue-500">
              {sessions.filter((s) => s.status === "active").length}
            </CardTitle>
          </CardHeader>
        </Card>
        <Card
          className={`cursor-pointer transition-colors ${
            currentStatus === "completed"
              ? "ring-2 ring-primary"
              : "hover:bg-muted/50"
          }`}
          onClick={() => handleStatusFilter("completed")}
        >
          <CardHeader className="pb-2">
            <CardDescription>
              {t("status.completed", "Completed")}
            </CardDescription>
            <CardTitle className="text-3xl text-green-500">
              {sessions.filter((s) => s.status === "completed").length}
            </CardTitle>
          </CardHeader>
        </Card>
        <Card
          className={`cursor-pointer transition-colors ${
            currentStatus === "cancelled"
              ? "ring-2 ring-primary"
              : "hover:bg-muted/50"
          }`}
          onClick={() => handleStatusFilter("cancelled")}
        >
          <CardHeader className="pb-2">
            <CardDescription>
              {t("status.cancelled", "Cancelled")}
            </CardDescription>
            <CardTitle className="text-3xl text-muted-foreground">
              {sessions.filter((s) => s.status === "cancelled").length}
            </CardTitle>
          </CardHeader>
        </Card>
      </div>

      {/* Search and View Toggle */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <form onSubmit={handleSearch} className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t("common.search", "Search...")}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="pl-9"
          />
        </form>

        {/* View toggle */}
        <div className="flex items-center border rounded-lg p-1">
          <Button
            variant={viewMode === "grid" ? "secondary" : "ghost"}
            size="sm"
            className="h-8 px-2"
            onClick={() => setViewMode("grid")}
          >
            <LayoutGrid className="h-4 w-4" />
          </Button>
          <Button
            variant={viewMode === "list" ? "secondary" : "ghost"}
            size="sm"
            className="h-8 px-2"
            onClick={() => setViewMode("list")}
          >
            <List className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Sessions Grid/Table */}
      {renderContent()}

      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      )}

      {/* Start Session Modal */}
      <Dialog open={showStartModal} onOpenChange={setShowStartModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Start In-Person Signing Session</DialogTitle>
            <DialogDescription>
              Select a document and start a session where signers can sign on
              your device. You can use documents that haven't been sent yet -
              perfect for in-person signing without emails.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Select Document</Label>
              {envelopes.length === 0 ? (
                <div className="rounded-lg border border-dashed p-4 text-center">
                  <p className="text-sm font-medium">No documents available</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    You need a document with recipients to start an in-person
                    signing session.
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Create a document and add recipients from the Documents page
                    (no need to send it).
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-3"
                    onClick={() => {
                      setShowStartModal(false);
                      navigate("/envelopes");
                    }}
                  >
                    Go to Documents
                  </Button>
                </div>
              ) : (
                <Select
                  value={selectedEnvelope}
                  onValueChange={setSelectedEnvelope}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a document..." />
                  </SelectTrigger>
                  <SelectContent>
                    {envelopes.map((env) => (
                      <SelectItem key={env.id} value={env.id}>
                        <div className="flex items-center gap-2">
                          <span>{env.title}</span>
                          <span
                            className={`text-xs px-1.5 py-0.5 rounded ${
                              env.status === "draft"
                                ? "bg-yellow-100 text-yellow-700"
                                : "bg-green-100 text-green-700"
                            }`}
                          >
                            {env.status === "draft" ? "Not sent" : env.status}
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {envelopes.length > 0 && (
              <div className="space-y-2">
                <Label>Your Name (Host)</Label>
                <Input
                  placeholder="Enter your name..."
                  value={hostName}
                  onChange={(e) => setHostName(e.target.value)}
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowStartModal(false)}
              size={isMobile ? "mobile" : "default"}
            >
              Cancel
            </Button>
            <Button
              onClick={startSession}
              disabled={
                !selectedEnvelope ||
                startSessionMutation.isPending ||
                envelopes.length === 0
              }
              size={isMobile ? "mobile" : "default"}
            >
              {startSessionMutation.isPending ? "Starting..." : "Start Session"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* QR Code Modal */}
      <Dialog open={!!showQRModal} onOpenChange={() => setShowQRModal(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Session QR Code</DialogTitle>
            <DialogDescription>
              Signers can scan this QR code to join the signing session
            </DialogDescription>
          </DialogHeader>

          <div className="flex justify-center py-6">
            {showQRModal && (
              <div className="p-4 bg-background rounded-lg">
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(
                    showQRModal.url,
                  )}`}
                  alt="QR Code"
                  className="w-48 h-48"
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowQRModal(null)}
              size={isMobile ? "mobile" : "default"}
            >
              Close
            </Button>
            <Button
              onClick={() => copyUrl(showQRModal)}
              size={isMobile ? "mobile" : "default"}
            >
              <Copy className="mr-2 h-4 w-4" />
              Copy URL
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InPersonSigning;
